import { ReactNode, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { FormikContextType, useFormik } from 'formik';
import {
	Box,
	Button,
	Card,
	CardContent,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tab,
	Tabs,
} from '@mui/material';
import { Page, PageContentList } from 'shared/_components/page/styles';
import PageHeader from 'shared/_components/page/page-header';
import { CardHeaderWithActions } from 'shared/_pages/account/styles';
import EditDocumentProvider, { useEditDocumentContext } from 'shared/hooks/use-edit-document';
import { SeriesContext } from 'shared/Providers/Series';
import { UserContext } from 'shared/Providers/User';
import { SERIES_SCHEMA } from 'shared/constants/general';
import { TDocSeries } from 'shared/constants/docs';
import { ROUTE } from 'shared/constants/routes_old';
import { r } from 'shared/constants/routes';
import useTabs from 'shared/components/Tabs/use-tabs';
import TabPanel from 'shared/components/Tabs/TabPanel';
import YoutubeAnalytics from 'shared/components/YoutubeAnalytics/YoutubeAnalytics';
import { ANALYTICS_TYPES } from 'shared/utils/youtube';
import { SeriesRssSettings } from 'shared/components/SeriesRssSettings';
import { IconMoreVert } from 'shared/_ui/icon';
import useMenu from 'shared/hooks/use-menu';
import useToggle from 'shared/hooks/use-toggle';
import { faRss } from '@fortawesome/free-solid-svg-icons';
import { Icon } from 'shared/components/Icon';
import SeriesImage from 'shared/Pages/common/Series/SeriesDetails/SeriesDetails/series-image';
import SeriesCard from 'shared/Pages/common/Series/SeriesDetails/SeriesDetails/series-card';
import SeriesMetadata from 'shared/Pages/common/Series/SeriesDetails/SeriesDetails/series-metadata';

function SeriesPageContent({
	formik,
	children,
}: {
	formik: FormikContextType<TDocSeries>;
	children?: ReactNode;
}) {
	const { series, deleteSeries, rssActivated } = useContext(SeriesContext);
	const {
		permissions: { canPublishMedia, canUseApp },
	} = useContext(UserContext);
	const { isEdit, toggleEdit } = useEditDocumentContext();
	const history = useHistory();
	const [tab, handleTabChange] = useTabs();
	const { anchorEl, open, handleOpen, handleClose } = useMenu();
	const [showRssSettings, toggleShowRssSettings] = useToggle();

	let rssIconColor = 'gray';
	if (rssActivated && series.seriesType === 'video') rssIconColor = 'orange';
	else if (rssActivated) rssIconColor = 'green';

	const handleDelete = useCallback(async () => {
		handleClose();
		const answer = window.confirm(
			'Series will be removed with all related Seasons and Episodes.\nContinue?'
		);
		if (answer) {
			if (await deleteSeries()) {
				history.push(ROUTE.series.root);
			}
		}
	}, [deleteSeries, handleClose, history]);

	const action = !canUseApp ? undefined : (
		<>
			{!isEdit && <Button onClick={() => toggleEdit(formik.resetForm)}>Edit</Button>}
			{isEdit && (
				<Button color="secondary" onClick={() => toggleEdit(formik.resetForm)}>
					Cancel
				</Button>
			)}
			{isEdit && (
				<Button
					onClick={formik.submitForm}
					disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
				>
					Save
				</Button>
			)}
			<IconButton onClick={handleOpen}>
				<IconMoreVert />
			</IconButton>
		</>
	);

	return (
		<Page sx={{ bgcolor: '#f8fafc' }}>
			<PageHeader breadcrumbs={[{ label: 'Series', href: r.series.path }]}>
				{series.title}
			</PageHeader>
			<PageContentList>
				<Box sx={{ paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 6, mb: 8 }}>
					<Card>
						<CardHeaderWithActions title={formik.values.title} action={action} />
						<CardContent>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									gap: 4,
								}}
							>
								<SeriesImage formik={formik} isEdit={isEdit} />
								<SeriesCard formik={formik} />
							</Box>
							<Tabs value={tab} onChange={handleTabChange} sx={{ mt: 5, mb: 7 }}>
								<Tab label="Metadata" />
								<Tab label="Analytics" />
							</Tabs>
							<TabPanel value={tab} index={0}>
								<SeriesMetadata formik={formik} />
							</TabPanel>
							<TabPanel value={tab} index={1}>
								<YoutubeAnalytics
									showTitle
									analyticType={ANALYTICS_TYPES.channel}
									id={series.youtube?.channelId}
								/>
							</TabPanel>
						</CardContent>
					</Card>
					{children}
				</Box>
			</PageContentList>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				{canPublishMedia && (
					<MenuItem
						onClick={() => {
							handleClose();
							toggleShowRssSettings();
						}}
					>
						<ListItemIcon>
							<span style={{ color: rssIconColor }}>
								<Icon icon={faRss} />
							</span>
						</ListItemIcon>
						<ListItemText>RSS</ListItemText>
					</MenuItem>
				)}
				<MenuItem disabled={!isEdit} onClick={handleDelete} sx={{ color: 'error.main' }}>
					<ListItemText>Delete</ListItemText>
				</MenuItem>
			</Menu>
			{showRssSettings && <SeriesRssSettings onClose={toggleShowRssSettings} />}
		</Page>
	);
}

export function SeriesDetails({ children }: { children?: ReactNode }) {
	const { series, saveSeries } = useContext(SeriesContext);
	const { resetEdit } = useEditDocumentContext();

	const handleSubmit = useCallback(
		async (values: Partial<TDocSeries>) => {
			await saveSeries(values);
			resetEdit();
		},
		[saveSeries, resetEdit]
	);

	const formik = useFormik<TDocSeries>({
		initialValues: series,
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		validationSchema: SERIES_SCHEMA,
		enableReinitialize: true,
		onSubmit: handleSubmit,
	});

	return <SeriesPageContent formik={formik}>{children}</SeriesPageContent>;
}

export function SeriesDetailsPage({ children }: { children?: ReactNode }) {
	return (
		<EditDocumentProvider>
			<SeriesDetails>{children}</SeriesDetails>
		</EditDocumentProvider>
	);
}
